'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { VStack, Box, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/styled/text';
import { MarkdownPreview } from '../../markdown-preview';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import {
  isRenderableToolName,
  type TraceMessage,
  type TraceMessageContent,
  type TracePhase,
} from './types';
import { TraceToolCard } from './TraceToolCard';
import { TraceReasoningCard } from './components/TraceReasoningCard';
import { normalizeToolName } from './utils/tool-config';
import { getToolCategory } from './utils/tool-categories';
import { PythonArtifactGroup } from '../../artifacts/python/PythonArtifact';
import { TraceReadToolCardGroup } from './components/TraceReadToolCardGroup';
import { TraceMcpToolCardGroup } from './components/TraceMcpToolCardGroup';
import { MessageSkeletonCard } from '../components/MessageSkeletonCard';
import { messageContainsOperationId } from './utils/highlight-operation';
import { createHideOnContextMenuHandler } from './utils/hide-on-context-menu';
import type { SseSubscribe } from '@terra/shared-sse-contracts';

type TraceMessageCardProps = {
  message: TraceMessage;
  isFirstUserMessage?: boolean;
  tracePhase?: TracePhase;
  preferHarOverToolOutput?: boolean;
  highlightOperationId?: string;
  storyStepVisible?: boolean;
  onToggleStepVisibility?: () => void;
  onSSESubscribe?: SseSubscribe;
};
const MESSAGE_STACK_GAP = 3;

type ToolCallContent = Extract<
  TraceMessageContent,
  {
    type: 'tool_call';
  }
>;
type AssistantRenderItem =
  | {
      type: 'single';
      content: TraceMessageContent;
    }
  | {
      type: 'python-group';
      contents: ToolCallContent[];
    }
  | {
      type: 'read-group';
      contents: ToolCallContent[];
    }
  | {
      type: 'mcp-group';
      contents: ToolCallContent[];
    };

function isReadToolCallForGrouping({
  content,
  tracePhase,
}: {
  content: ToolCallContent;
  tracePhase: TracePhase;
}): boolean {
  if (normalizeToolName(content.toolCall.name) !== 'read-file') {
    return false;
  }

  return tracePhase === 'post-attack' || tracePhase === 'co-pilot';
}

function isQuietMcpToolCallForGrouping(content: ToolCallContent): boolean {
  return (
    normalizeToolName(content.toolCall.name) === 'ai-discovery-mcp' &&
    !content.toolCall.error
  );
}

function isPythonExecutionToolCall(content: ToolCallContent): boolean {
  const normalizedName = normalizeToolName(content.toolCall.name);

  if (getToolCategory(content.toolCall.name) !== 'code-execution') {
    return false;
  }

  return (
    normalizedName === 'coder' ||
    normalizedName === 'coder-tool' ||
    normalizedName === 'python-tool' ||
    normalizedName === 'sub-agent-coder'
  );
}

function shouldRenderToolCallContent({
  content,
  tracePhase,
}: {
  content: ToolCallContent;
  tracePhase: TracePhase;
}): boolean {
  if (!isRenderableToolName(content.toolCall.name, tracePhase)) {
    return false;
  }

  const normalizedName = normalizeToolName(content.toolCall.name);

  if (normalizedName === 'todo-write') {
    return content.toolCall.args?.merge === true;
  }

  return true;
}

function shouldRenderContent({
  content,
  tracePhase,
}: {
  content: TraceMessageContent;
  tracePhase: TracePhase;
}): boolean {
  switch (content.type) {
    case 'text':
    case 'reasoning':
      return content.text.trim().length > 0;
    case 'tool_call':
      return shouldRenderToolCallContent({ content, tracePhase });
    case 'tool_result':
      return false;
    default:
      return false;
  }
}

function getAssistantRenderItems({
  contents,
  tracePhase,
  preferHarOverToolOutput = false,
}: {
  contents: TraceMessageContent[];
  tracePhase: TracePhase;
  preferHarOverToolOutput?: boolean;
}): AssistantRenderItem[] {
  const items: AssistantRenderItem[] = [];
  let currentPythonGroup: ToolCallContent[] = [];
  let currentReadGroup: ToolCallContent[] = [];
  let currentMcpGroup: ToolCallContent[] = [];
  const flushPythonGroup = () => {
    if (currentPythonGroup.length === 0) {
      return;
    }

    if (currentPythonGroup.length === 1 && currentPythonGroup[0]) {
      items.push({ type: 'single', content: currentPythonGroup[0] });
    } else {
      items.push({ type: 'python-group', contents: currentPythonGroup });
    }

    currentPythonGroup = [];
  };

  const flushReadGroup = () => {
    if (currentReadGroup.length === 0) {
      return;
    }

    items.push({ type: 'read-group', contents: currentReadGroup });
    currentReadGroup = [];
  };

  const flushMcpGroup = () => {
    if (currentMcpGroup.length === 0) {
      return;
    }

    items.push({ type: 'mcp-group', contents: currentMcpGroup });
    currentMcpGroup = [];
  };

  for (const content of contents) {
    if (!shouldRenderContent({ content, tracePhase })) {
      continue;
    }

    if (
      content.type === 'tool_call' &&
      isReadToolCallForGrouping({ content, tracePhase })
    ) {
      flushPythonGroup();
      flushMcpGroup();
      currentReadGroup.push(content);
      continue;
    }

    if (content.type === 'tool_call' && isPythonExecutionToolCall(content)) {
      flushReadGroup();
      flushMcpGroup();
      currentPythonGroup.push(content);
      continue;
    }

    if (
      preferHarOverToolOutput &&
      content.type === 'tool_call' &&
      isQuietMcpToolCallForGrouping(content)
    ) {
      flushPythonGroup();
      flushReadGroup();
      currentMcpGroup.push(content);
      continue;
    }

    flushPythonGroup();
    flushReadGroup();
    flushMcpGroup();
    items.push({ type: 'single', content });
  }

  flushPythonGroup();
  flushReadGroup();
  flushMcpGroup();

  return items;
}

function getAssistantRenderItemKey(
  item: AssistantRenderItem,
  index: number,
): string {
  if (item.type === 'python-group') {
    return `python-group-${item.contents.map((content) => content.toolCall.id).join('-')}-${index}`;
  }

  if (item.type === 'read-group') {
    return `read-group-${item.contents.map((content) => content.toolCall.id).join('-')}-${index}`;
  }

  if (item.type === 'mcp-group') {
    return `mcp-group-${item.contents.map((content) => content.toolCall.id).join('-')}-${index}`;
  }

  if (item.content.type === 'tool_call') {
    return `tool-${item.content.toolCall.id}-${index}`;
  }

  return `${item.content.type}-${index}`;
}

function TraceTextCard({
  text,
  tracePhase,
}: {
  text: string;
  tracePhase: TracePhase;
}) {
  if (tracePhase === 'co-pilot') {
    return <MessageSkeletonCard type="assistant" markdownSource={text} />;
  }

  return (
    <VStack alignItems="start" gap={2} width="100%">
      <Box width="100%">
        <MarkdownPreview source={text} fontSize={13} />
      </Box>
    </VStack>
  );
}

function ContentBlock({
  content,
  tracePhase,
  preferHarOverToolOutput,
  onToggleStepVisibility,
  onSSESubscribe,
}: {
  content: TraceMessageContent;
  tracePhase: TracePhase;
  preferHarOverToolOutput?: boolean;
  onToggleStepVisibility?: () => void;
  onSSESubscribe?: SseSubscribe;
}) {
  switch (content.type) {
    case 'text':
      return (
        <Box
          width="100%"
          onContextMenu={createHideOnContextMenuHandler(onToggleStepVisibility)}
          cursor={onToggleStepVisibility ? 'context-menu' : undefined}
        >
          <TraceTextCard text={content.text} tracePhase={tracePhase} />
        </Box>
      );
    case 'reasoning': {
      return (
        <Box
          width="100%"
          onContextMenu={createHideOnContextMenuHandler(onToggleStepVisibility)}
          cursor={onToggleStepVisibility ? 'context-menu' : undefined}
        >
          <TraceReasoningCard
            text={content.text}
            defaultOpen={Boolean(onToggleStepVisibility)}
          />
        </Box>
      );
    }

    case 'tool_call':
      return (
        <Box width="100%">
          <TraceToolCard
            toolCall={content.toolCall}
            tracePhase={tracePhase}
            preferHarOverToolOutput={preferHarOverToolOutput}
            onHideMessage={onToggleStepVisibility}
            onSSESubscribe={onSSESubscribe}
          />
        </Box>
      );
    case 'tool_result':
      return null;
    default:
      return null;
  }
}

function UserMessageCard({
  message,
  isCollapsible = false,
  tracePhase,
}: {
  message: TraceMessage;
  isCollapsible?: boolean;
  tracePhase: TracePhase;
}) {
  const t = useTranslations('designSystem.chat');
  const [isCollapsed, setIsCollapsed] = useState(isCollapsible);
  const textContent = message.content.filter((c) => c.type === 'text');

  if (textContent.length === 0) return null;
  const combinedText = textContent
    .map((content) => (content.type === 'text' ? content.text : ''))
    .join('\n\n')
    .trim();

  if (tracePhase === 'co-pilot') {
    return <MessageSkeletonCard type="user" markdownSource={combinedText} />;
  }

  const firstContent = textContent[0];
  const previewText =
    firstContent?.type === 'text'
      ? firstContent.text.split('\n')[0].substring(0, 100)
      : '';

  return (
    <Box
      width="100%"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="bg.default"
      overflow="hidden"
    >
      <HStack
        gap={2}
        width="100%"
        alignItems="center"
        py={2}
        px={3}
        cursor={isCollapsible ? 'pointer' : 'default'}
        onClick={isCollapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
        borderLeftWidth="3px"
        borderLeftColor="border.accent"
        _hover={isCollapsible ? { bg: 'bg.muted' } : undefined}
      >
        {isCollapsible && (
          <Box display="flex" alignItems="center" color="fg.muted">
            {isCollapsed ? (
              <ArrowRight01Icon size={14} />
            ) : (
              <ArrowDown01Icon size={14} />
            )}
          </Box>
        )}
        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
          {t('user')}
        </Text>
        {isCollapsible && isCollapsed && (
          <Text fontSize="sm" color="fg.muted" truncate flex={1} minWidth={0}>
            {previewText}...
          </Text>
        )}
      </HStack>
      {!isCollapsed && (
        <Box
          width="100%"
          px={4}
          py={3}
          borderTopWidth="1px"
          borderTopColor="border.subtle"
        >
          <VStack gap={MESSAGE_STACK_GAP} alignItems="stretch" width="100%">
            {textContent.map((content, index) => (
              <ContentBlock
                key={index}
                content={content}
                tracePhase={tracePhase}
              />
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

const TOOL_CALLS_ONLY_AGENT_NAMES = new Set([
  'sub-agent-intelligence',
  'operator-sub-agent',
  'sub-agent-operator',
]);

function AssistantMessageCard({
  message,
  tracePhase,
  preferHarOverToolOutput,
  onToggleStepVisibility,
  onSSESubscribe,
}: {
  message: TraceMessage;
  tracePhase: TracePhase;
  preferHarOverToolOutput?: boolean;
  onToggleStepVisibility?: () => void;
  onSSESubscribe?: SseSubscribe;
}) {
  const normalizedAgentName = message.agentName
    ? normalizeToolName(message.agentName)
    : '';
  const shouldRenderToolCallsOnly =
    tracePhase !== 'co-pilot' &&
    TOOL_CALLS_ONLY_AGENT_NAMES.has(normalizedAgentName);
  const contentToRender = shouldRenderToolCallsOnly
    ? message.content.filter(
        (
          content,
        ): content is Extract<
          TraceMessageContent,
          {
            type: 'tool_call';
          }
        > => content.type === 'tool_call',
      )
    : message.content;
  const renderItems = getAssistantRenderItems({
    contents: contentToRender,
    tracePhase,
    preferHarOverToolOutput,
  });

  if (renderItems.length === 0) {
    return null;
  }

  return (
    <VStack gap={MESSAGE_STACK_GAP} alignItems="stretch" width="100%">
      {renderItems.map((item, index) => {
        const key = getAssistantRenderItemKey(item, index);

        if (item.type === 'python-group') {
          return (
            <Box key={key} width="100%">
              <PythonArtifactGroup
                toolCalls={item.contents.map((content) => content.toolCall)}
                tracePhase={tracePhase}
              />
            </Box>
          );
        }

        if (item.type === 'read-group') {
          return (
            <Box key={key} width="100%">
              <TraceReadToolCardGroup
                toolCalls={item.contents.map((content) => content.toolCall)}
                tracePhase={tracePhase}
              />
            </Box>
          );
        }

        if (item.type === 'mcp-group') {
          return (
            <Box key={key} width="100%">
              <TraceMcpToolCardGroup
                toolCalls={item.contents.map((content) => content.toolCall)}
                tracePhase={tracePhase}
                preferHarOverToolOutput={preferHarOverToolOutput}
              />
            </Box>
          );
        }

        return (
          <ContentBlock
            key={key}
            content={item.content}
            tracePhase={tracePhase}
            preferHarOverToolOutput={preferHarOverToolOutput}
            onToggleStepVisibility={
              item.content.type === 'tool_call' ||
              item.content.type === 'text' ||
              item.content.type === 'reasoning'
                ? onToggleStepVisibility
                : undefined
            }
            onSSESubscribe={onSSESubscribe}
          />
        );
      })}
    </VStack>
  );
}

export function TraceMessageCard({
  message,
  isFirstUserMessage = false,
  tracePhase = 'attack',
  preferHarOverToolOutput,
  highlightOperationId,
  storyStepVisible = true,
  onToggleStepVisibility,
  onSSESubscribe,
}: TraceMessageCardProps) {
  const isHighlighted = Boolean(
    highlightOperationId &&
      messageContainsOperationId({
        message,
        operationId: highlightOperationId,
      }),
  );
  const content =
    message.role === 'user' ? (
      <UserMessageCard
        message={message}
        isCollapsible={tracePhase !== 'co-pilot' && isFirstUserMessage}
        tracePhase={tracePhase}
      />
    ) : message.role === 'assistant' ? (
      <AssistantMessageCard
        message={message}
        tracePhase={tracePhase}
        preferHarOverToolOutput={preferHarOverToolOutput}
        onToggleStepVisibility={onToggleStepVisibility}
        onSSESubscribe={onSSESubscribe}
      />
    ) : null;

  if (!content) {
    return null;
  }

  return (
    <Box
      w="full"
      opacity={storyStepVisible ? undefined : 0.45}
      data-highlight-operation-id={
        isHighlighted ? highlightOperationId : undefined
      }
      borderWidth={isHighlighted ? '1px' : undefined}
      borderColor={isHighlighted ? 'border.accent' : undefined}
      borderRadius={isHighlighted ? 'md' : undefined}
      bg={isHighlighted ? 'bg.emphasis' : undefined}
      p={isHighlighted ? 1 : undefined}
      animation={isHighlighted ? 'fade-in 0.3s ease-out' : undefined}
    >
      {content}
    </Box>
  );
}
